import React, { FC, useCallback, useContext } from 'react';
import { PageHeader } from '../components/PageHeader/PageHeader';
import { OrganizationFetcher } from '../components/Customer/CustomerFetchers';
import { OrganizationUserFetcher } from '../fetchers/OrganizationUserFetcher';
import { GuardedRouteLink, useIsGuardedRouteAllowed } from '../resources/GuardedRouteLink';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { AccountSingleData } from '../components/AccountData/AccountSingleData';
import Link from '@mui/material/Link';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { apiOrganization } from '../resources/api/apiOrganization';
import { useDialog } from '../hooks/useDialog';
import Button from '@mui/material/Button';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { NotificationContext } from '../contexts/NotificationContext';
import { UserContextFromOrganizationUserProvider } from '../contexts/UserContextProvider';
import { UserLicensesFetcher } from '../fetchers/UserLicensesFetcher';
import { UserLicenses } from '../components/Licenses/UserLicenses';
import { RequestAirgapButton } from '../components/Licenses/RequestAirgapButton';
import { LicensesContextFromUserLicenses } from '../contexts/LicensesContextProviders';
import { AssignLicensesButton } from '../components/AssignLicensesButton/AssignLicensesButton';
import { styled } from '@mui/material';
import { useAutoFetch } from '../contexts/Fetcher';
import { LicensesContext } from '../contexts/LicensesContext';
import { ELicenseMode } from '../models/ILicense';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { WarningAlert } from '../components/Alert/WarningAlert';
import Box from '@mui/system/Box';
import { useEnumState } from '../hooks/useEnumState';
import Alert from '@mui/material/Alert';
import { DangerousButton } from '../components/CustomButtons/DangerousButton';

const { useRemoveUser, useGetOrganizations } = apiOrganization;

const ButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'end',
  gap: theme.spacing(1),
  padding: `${theme.spacing(2)} 0`,
}));

const UserOrganization: FC = () => {
  const handleError = useApiErrorHandler();

  const history = useHistory();

  const { organization } = useContext(OrganizationContext);
  const { data } = useContext(OrganizationUserFetcher.Context);
  const { licenses } = useContext(LicensesContext);

  const anyLicenses = licenses ? licenses.length > 0 : null;
  const anyOffline = (licenses ?? []).some((l) => [ELicenseMode.Offline, ELicenseMode.Airgap].includes(l.mode));
  const [offlineConfirmation, openOfflineConfirmation, resetOfflineConfirmation] = useEnumState(false, true, false);

  const { t } = useTranslation();
  useAutoFetch(OrganizationFetcher);

  const isNoOrgUserAllowed = useIsGuardedRouteAllowed('User', ['any']);

  const { execute: removeUser, Guard: GuardRemoveUser } = useRemoveUser(organization?.name, data?.payload.id);
  const { Guard: GuardOrganizations } = useGetOrganizations();

  const { isOpen: isDeleting, openUrl: deleteUrl, close: abortDelete } = useDialog('delete');

  const { addNotification } = useContext(NotificationContext);

  const user = data?.payload;
  const userName = `${user?.first_name} ${user?.last_name}`;

  const onRemoveFromOrg = useCallback(async () => {
    if (!user || !organization) return;
    try {
      await removeUser();
      resetOfflineConfirmation();
      addNotification({
        severity: 'success',
        message: t('user.removeFromOrganization.success', {
          name: `${user.first_name} ${user.last_name}`,
          organization: organization.name,
        }),
      });
      if (isNoOrgUserAllowed) {
        history.replace(AuthenticatedRoutes.User(user.id));
      } else {
        history.replace(AuthenticatedRoutes.CurrentOrganizationUsers());
      }
    } catch (e) {
      handleError(e);
    }
  }, [isNoOrgUserAllowed, data?.payload]);

  if (!user || !organization) return null;

  return (
    <div>
      <PageHeader
        title={isNoOrgUserAllowed ? organization.name : userName}
        crumbs={
          isNoOrgUserAllowed
            ? [
                {
                  title: t('layout.navigation.users'),
                  url: AuthenticatedRoutes.AllUsers(),
                },
                {
                  title: userName,
                  url: AuthenticatedRoutes.User(user.id),
                },
              ]
            : [
                {
                  title: t('layout.navigation.organizationUsers'),
                  url: AuthenticatedRoutes.CurrentOrganizationUsers(),
                },
              ]
        }
      >
        <GuardRemoveUser>
          <Button variant="outlined" component={RouterLink} to={deleteUrl} replace={true}>
            {t('user.removeFromOrganization.button')}
          </Button>

          <Dialog open={isDeleting}>
            <DialogTitle>{t('user.removeFromOrganization.button')}</DialogTitle>

            {offlineConfirmation ? (
              <DialogContent>
                <Alert severity="error">{t('user.removeFromOrganization.anyOfflineWarning', { userName })}</Alert>
              </DialogContent>
            ) : (
              <DialogContent>
                {typeof anyLicenses === 'undefined'
                  ? '...'
                  : t('user.removeFromOrganization.message', {
                      name: userName,
                      organization: organization.name,
                    })}

                {anyLicenses === true ? (
                  <Box mt={2}>
                    <WarningAlert>{t('user.removeFromOrganization.anyLicensesWarning')}</WarningAlert>
                  </Box>
                ) : null}
              </DialogContent>
            )}
            <DialogActions>
              <Button
                variant={'text'}
                onClick={() => {
                  abortDelete();
                  resetOfflineConfirmation();
                }}
              >
                {t('common.dialog.close')}
              </Button>
              {offlineConfirmation ? (
                <DangerousButton variant={'contained'} onClick={onRemoveFromOrg}>
                  {t('user.removeFromOrganization.ignoreWarningButton')}
                </DangerousButton>
              ) : (
                <Button
                  variant={'contained'}
                  onClick={anyOffline ? openOfflineConfirmation : onRemoveFromOrg}
                  disabled={typeof anyLicenses === 'undefined'}
                >
                  {t('common.dialog.submit')}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </GuardRemoveUser>
      </PageHeader>

      <Grid container spacing={1}>
        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.name')}>
            {data.payload.first_name} {data.payload.last_name}
          </AccountSingleData>
        </Grid>

        <GuardOrganizations>
          <Grid item xs={6} md={4} lg={3}>
            <AccountSingleData label={t('common.tableHeader.organization')}>
              <Link
                component={GuardedRouteLink}
                route={'OrganizationDetails'}
                params={[organization.name]}
                showFallback
              >
                {organization.name}
              </Link>
            </AccountSingleData>
          </Grid>
        </GuardOrganizations>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.email')}>{data.payload.email}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.phone')}>{data.payload.phone_number}</AccountSingleData>
        </Grid>

        <Grid item xs={6} md={4} lg={3}>
          <AccountSingleData label={t('common.tableHeader.id')}>{data.payload.id}</AccountSingleData>
        </Grid>
      </Grid>

      <ButtonsWrapper>
        <AssignLicensesButton />
        <RequestAirgapButton />
      </ButtonsWrapper>

      <UserLicenses />
    </div>
  );
};

export const UserOrganizationPage: FC = () => {
  return (
    <OrganizationUserFetcher.WAF>
      <UserContextFromOrganizationUserProvider>
        <UserLicensesFetcher.WAF>
          <LicensesContextFromUserLicenses>
            <UserOrganization />
          </LicensesContextFromUserLicenses>
        </UserLicensesFetcher.WAF>
      </UserContextFromOrganizationUserProvider>
    </OrganizationUserFetcher.WAF>
  );
};
